import { supabase } from './supabase'
import type { ChatMessage, ChatMeta, Listing, Order, Product, Store, UserProfile } from './types'

type Unsubscribe = () => void

/* ---------------- Базар ---------------- */

export interface ListingFilters {
  q?: string
  category?: string
  brand?: string
  city?: string
  condition?: string
  minPrice?: number
  maxPrice?: number
}

/** Postgres тянет все фильтры сервер-сайд + русский полнотекстовый поиск */
export async function fetchListings(f: ListingFilters, max = 60): Promise<Listing[]> {
  let q = supabase.from('listings').select('*').eq('status', 'active')
  if (f.q) q = q.textSearch('fts', f.q, { type: 'websearch', config: 'russian' })
  if (f.category) q = q.eq('category', f.category)
  if (f.brand) q = q.eq('brand', f.brand)
  if (f.city) q = q.eq('city', f.city)
  if (f.condition) q = q.eq('condition', f.condition)
  if (f.minPrice != null) q = q.gte('price', f.minPrice)
  if (f.maxPrice != null) q = q.lte('price', f.maxPrice)
  const { data, error } = await q.order('bumpedAt', { ascending: false }).limit(max)
  if (error) throw error
  return (data as Listing[]) || []
}

export async function getListing(id: string): Promise<Listing | null> {
  const { data } = await supabase.from('listings').select('*').eq('id', id).maybeSingle()
  if (data) supabase.rpc('increment_views', { lid: id }).then(() => {})
  return (data as Listing) || null
}

/**
 * Сжатие фото на клиенте: до 1600px по длинной стороне, JPEG ~82%.
 * Телефонное фото 5МБ превращается в ~300КБ — бережём бесплатный 1ГБ Storage.
 * Если формат не читается (например HEIC в некоторых браузерах) — грузим оригинал.
 */
async function compressImage(file: Blob, maxSide = 1600, quality = 0.82): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height))
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, w, h)
    bitmap.close()
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', quality))
    return blob && blob.size < file.size ? blob : file
  } catch {
    return file
  }
}

export async function uploadPhotos(uid: string, files: File[], bucket = 'listings'): Promise<string[]> {
  const urls: string[] = []
  for (const file of files.slice(0, 8)) {
    const blob = await compressImage(file)
    const compressed = blob !== file
    const ext = compressed ? 'jpg' : (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg'
    const path = `${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
    const { error } = await supabase.storage.from(bucket).upload(path, blob, {
      contentType: compressed ? 'image/jpeg' : file.type || 'image/jpeg',
      upsert: false,
    })
    if (error) throw error
    urls.push(supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl)
  }
  return urls
}

export async function createListing(
  data: Omit<Listing, 'id' | 'views' | 'createdAt' | 'bumpedAt' | 'status' | 'currency'>,
): Promise<string> {
  const { data: row, error } = await supabase
    .from('listings')
    .insert({ ...data, currency: 'KGS', status: 'active' })
    .select('id')
    .single()
  if (error) throw error
  return row.id as string
}

export async function setListingStatus(id: string, status: 'active' | 'sold' | 'archived') {
  const { error } = await supabase.from('listings').update({ status }).eq('id', id)
  if (error) throw error
}

export async function bumpListing(id: string) {
  const { error } = await supabase.from('listings').update({ bumpedAt: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}

export async function deleteListing(id: string) {
  const { error } = await supabase.from('listings').delete().eq('id', id)
  if (error) throw error
}

export async function myListings(uid: string): Promise<Listing[]> {
  const { data } = await supabase
    .from('listings').select('*').eq('sellerId', uid)
    .order('createdAt', { ascending: false }).limit(100)
  return (data as Listing[]) || []
}

/* ---------------- Профиль ---------------- */

export async function updateProfile(uid: string, fields: Partial<UserProfile>) {
  const { error } = await supabase.from('profiles').update(fields).eq('id', uid)
  if (error) throw error
}

/* ---------------- Чаты ---------------- */

export async function listGroupChats(): Promise<ChatMeta[]> {
  const { data } = await supabase
    .from('chats').select('*').eq('type', 'group')
    .order('lastMsgAt', { ascending: false, nullsFirst: false }).limit(50)
  return (data as ChatMeta[]) || []
}

export function subscribeMyDms(uid: string, cb: (chats: ChatMeta[]) => void): Unsubscribe {
  async function load() {
    const { data } = await supabase
      .from('chats').select('*').eq('type', 'dm').contains('members', [uid])
      .order('lastMsgAt', { ascending: false, nullsFirst: false }).limit(50)
    cb((data as ChatMeta[]) || [])
  }
  load()
  // уникальное имя канала: компоненты могут подписываться параллельно (навигация + страница чатов)
  const channel = supabase
    .channel(`dms-${uid}-${Math.random().toString(36).slice(2, 9)}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'chats' }, () => load())
    .subscribe()
  return () => {
    supabase.removeChannel(channel)
  }
}

/** Все группы, где я участник (для «мои группы») */
export async function myGroupIds(uid: string): Promise<string[]> {
  const { data } = await supabase
    .from('chats').select('id').eq('type', 'group').contains('members', [uid])
  return ((data as { id: string }[]) || []).map((r) => r.id)
}

/** Вступить в группу */
export async function joinGroup(chatId: string, displayName?: string) {
  const { error } = await supabase.rpc('join_group', {
    chat_id: chatId,
    display_name: displayName ?? null,
  })
  if (error) throw error
}

/** Выйти из группы */
export async function leaveGroup(chatId: string) {
  const { error } = await supabase.rpc('leave_group', { chat_id: chatId })
  if (error) throw error
}

/** Создать группу — RLS пропустит только админа */
export async function createGroup(input: {
  title: string
  region?: string
  topic?: string
  owner: { uid: string; name: string }
}): Promise<string> {
  const base = slugify(input.title) || 'group'
  const id = `g-${base}-${Math.random().toString(36).slice(2, 6)}`
  const { error } = await supabase.from('chats').insert({
    id,
    type: 'group',
    title: input.title.trim().slice(0, 60),
    region: input.region?.trim().slice(0, 40) || '',
    topic: input.topic?.trim().slice(0, 80) || '',
    members: [input.owner.uid],
    memberNames: { [input.owner.uid]: input.owner.name },
    lastMsgAt: new Date().toISOString(),
  })
  if (error) throw error
  return id
}

/** Удалить группу — RLS пропустит только админа */
export async function deleteGroup(chatId: string) {
  const { error } = await supabase.from('chats').delete().eq('id', chatId)
  if (error) throw error
}

export async function getChat(id: string): Promise<ChatMeta | null> {
  const { data } = await supabase.from('chats').select('*').eq('id', id).maybeSingle()
  return (data as ChatMeta) || null
}

/** DM-чат по паре пользователей: детерминированный id */
export async function ensureDmChat(
  me: { uid: string; name: string },
  other: { uid: string; name: string },
): Promise<string> {
  const id = ['dm', ...[me.uid, other.uid].sort()].join('_')
  const { data } = await supabase.from('chats').select('id').eq('id', id).maybeSingle()
  if (!data) {
    const { error } = await supabase.from('chats').insert({
      id,
      type: 'dm',
      title: '',
      members: [me.uid, other.uid],
      memberNames: { [me.uid]: me.name, [other.uid]: other.name },
      lastMsgAt: new Date().toISOString(),
    })
    if (error && !String(error.message).includes('duplicate')) throw error
  }
  return id
}

export function subscribeMessages(chatId: string, cb: (msgs: ChatMessage[]) => void): Unsubscribe {
  let msgs: ChatMessage[] = []
  async function load() {
    const { data } = await supabase
      .from('messages').select('*').eq('chatId', chatId)
      .order('createdAt', { ascending: true }).limit(200)
    msgs = (data as ChatMessage[]) || []
    cb(msgs)
  }
  load()
  const channel = supabase
    .channel(`chat-${chatId}-${Math.random().toString(36).slice(2, 9)}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'messages', filter: `chatId=eq.${chatId}` },
      (payload) => {
        const row = payload.new as ChatMessage
        if (payload.eventType === 'INSERT') {
          if (!msgs.some((m) => m.id === row.id)) msgs = [...msgs, row]
        } else if (payload.eventType === 'UPDATE') {
          // при DEFAULT replica identity в payload приходит только часть полей —
          // подстраховываемся мержем со старой версией
          msgs = msgs.map((m) => (m.id === row.id ? { ...m, ...row } : m))
        } else if (payload.eventType === 'DELETE') {
          const gone = payload.old as { id?: string }
          msgs = msgs.filter((m) => m.id !== gone?.id)
        }
        cb(msgs)
      },
    )
    .subscribe()
  return () => {
    supabase.removeChannel(channel)
  }
}

/** Загрузка медиа чата (фото сжимается, голос как есть) → публичный URL */
export async function uploadChatMedia(uid: string, blob: Blob, kind: 'image' | 'audio'): Promise<string> {
  let payload: Blob = blob
  let mime = (blob.type || 'application/octet-stream').split(';')[0]
  let ext = 'bin'
  if (kind === 'image') {
    payload = await compressImage(blob)
    if (payload !== blob) mime = 'image/jpeg'
    ext = mime === 'image/jpeg' ? 'jpg' : (mime.split('/')[1] || 'jpg')
  } else {
    ext = mime.includes('webm') ? 'webm' : mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm'
    if (mime.includes('mp4')) mime = 'audio/mp4'
  }
  const path = `${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
  const { error } = await supabase.storage.from('chat').upload(path, payload, { contentType: mime })
  if (error) throw error
  return supabase.storage.from('chat').getPublicUrl(path).data.publicUrl
}

export async function sendMessage(
  chatId: string,
  sender: { uid: string; name: string },
  text: string,
  media?: { imageUrl?: string; audioUrl?: string },
  replyTo?: { id: string; name: string; text: string } | null,
) {
  const clean = text.trim().slice(0, 2000)
  if (!clean && !media?.imageUrl && !media?.audioUrl) return
  const { error } = await supabase.from('messages').insert({
    chatId,
    senderId: sender.uid,
    senderName: sender.name,
    text: clean,
    imageUrl: media?.imageUrl ?? null,
    audioUrl: media?.audioUrl ?? null,
    replyToId: replyTo?.id ?? null,
    replyToName: replyTo?.name ?? null,
    replyToText: replyTo ? replyTo.text.slice(0, 140) : null,
  })
  if (error) throw error
  const preview = clean || (media?.imageUrl ? '📷 Фото' : '🎤 Голосовое')
  await supabase.from('chats')
    .update({ lastMsg: preview.slice(0, 80), lastMsgAt: new Date().toISOString() })
    .eq('id', chatId)
}

/** Удалить сообщение (автор или админ) — остаётся плашка «сообщение удалено» */
export async function deleteMessage(id: string) {
  const { error } = await supabase.from('messages').update({ deleted: true }).eq('id', id)
  if (error) throw error
}

/** Изменить текст своего сообщения */
export async function editMessage(id: string, text: string) {
  const clean = text.trim().slice(0, 2000)
  if (!clean) return
  const { error } = await supabase.from('messages').update({ text: clean }).eq('id', id)
  if (error) throw error
}

/** Задержка между сообщениями в группе (антифлуд), 0 — выключить. Только админ */
export async function setSlowmode(chatId: string, secs: number) {
  const { error } = await supabase.rpc('set_slowmode', { chat_id: chatId, secs })
  if (error) throw error
}

/** Закрепить сообщение (null — снять закреп). Только админ */
export async function pinMessage(chatId: string, msgId: string | null) {
  const { error } = await supabase.rpc('pin_message', { chat_id: chatId, msg_id: msgId })
  if (error) throw error
}

/** Изменения самого чата (закреп, slowmode, состав) в реальном времени */
export function subscribeChat(chatId: string, cb: (chat: ChatMeta) => void): Unsubscribe {
  const channel = supabase
    .channel(`chatmeta-${chatId}-${Math.random().toString(36).slice(2, 9)}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'chats', filter: `id=eq.${chatId}` },
      (payload) => cb(payload.new as ChatMeta),
    )
    .subscribe()
  return () => {
    supabase.removeChannel(channel)
  }
}

/* ---------------- Магазины ---------------- */

export async function listStores(): Promise<Store[]> {
  const { data } = await supabase.from('stores').select('*').order('createdAt', { ascending: false }).limit(50)
  return (data as Store[]) || []
}

export async function getStoreBySlug(slug: string): Promise<Store | null> {
  const { data } = await supabase.from('stores').select('*').eq('slug', slug).maybeSingle()
  return (data as Store) || null
}

export async function listProducts(storeId: string): Promise<Product[]> {
  const { data } = await supabase.from('products').select('*').eq('storeId', storeId).limit(200)
  return (data as Product[]) || []
}

export async function createOrder(o: Omit<Order, 'id' | 'status' | 'createdAt'>): Promise<string> {
  const { data, error } = await supabase
    .from('orders').insert({ ...o, status: 'new' }).select('id').single()
  if (error) throw error
  return data.id as string
}

/* ---------------- Жалобы ---------------- */

export async function reportTarget(
  targetType: 'listing' | 'user' | 'message',
  targetId: string,
  reason: string,
  byUid: string,
) {
  await supabase.from('reports').insert({ targetType, targetId, reason: reason.slice(0, 500), byUid })
}

/* ---------------- Избранное ---------------- */

export async function fetchFavoriteIds(uid: string): Promise<string[]> {
  const { data } = await supabase.from('favorites').select('listingId').eq('userId', uid).limit(500)
  return (data || []).map((r: { listingId: string }) => r.listingId)
}

export async function setFavorite(uid: string, listingId: string, on: boolean) {
  if (on) {
    await supabase.from('favorites').upsert({ userId: uid, listingId })
  } else {
    await supabase.from('favorites').delete().eq('userId', uid).eq('listingId', listingId)
  }
}

export async function fetchFavoriteListings(uid: string): Promise<Listing[]> {
  const ids = await fetchFavoriteIds(uid)
  if (ids.length === 0) return []
  const { data } = await supabase
    .from('listings').select('*').in('id', ids).neq('status', 'blocked')
    .order('bumpedAt', { ascending: false })
  return (data as Listing[]) || []
}

/* ---------------- Похожие объявления ---------------- */

export async function fetchSimilar(l: Listing, max = 4): Promise<Listing[]> {
  const { data } = await supabase
    .from('listings').select('*')
    .eq('status', 'active').eq('category', l.category).neq('id', l.id)
    .order('bumpedAt', { ascending: false }).limit(max)
  return (data as Listing[]) || []
}

/* ---------------- Прочитанность чатов ---------------- */

export async function fetchChatReads(uid: string): Promise<Record<string, string>> {
  const { data } = await supabase.from('chat_reads').select('chatId,lastReadAt').eq('userId', uid).limit(500)
  return Object.fromEntries((data || []).map((r: { chatId: string; lastReadAt: string }) => [r.chatId, r.lastReadAt]))
}

export async function markChatRead(uid: string, chatId: string) {
  await supabase.from('chat_reads').upsert({ userId: uid, chatId, lastReadAt: new Date().toISOString() })
}

/* ---------------- Создание магазина и товары ---------------- */

const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'i',
  к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
  х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  ң: 'n', ү: 'u', ө: 'o',
}

export function slugify(name: string): string {
  const s = name.toLowerCase().split('').map((c) => TRANSLIT[c] ?? c).join('')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
  return s || 'store'
}

export async function createStore(input: {
  name: string; desc: string; city: string; address?: string; phone: string; whatsapp?: string;
  ownerUid: string; logo?: string; cover?: string;
}): Promise<string> {
  let slug = slugify(input.name)
  for (let attempt = 0; attempt < 3; attempt++) {
    const trySlug = attempt === 0 ? slug : `${slug}-${Math.random().toString(36).slice(2, 6)}`
    const { data, error } = await supabase
      .from('stores').insert({ ...input, slug: trySlug, verified: false }).select('slug').single()
    if (!error) return data.slug as string
    if (!String(error.message).includes('duplicate')) throw error
  }
  throw new Error('slug conflict')
}

export async function myStore(uid: string): Promise<Store | null> {
  const { data } = await supabase.from('stores').select('*').eq('ownerUid', uid).limit(1).maybeSingle()
  return (data as Store) || null
}

export async function addProduct(p: {
  storeId: string; name: string; price: number; photos: string[]; inStock: boolean; desc?: string;
}): Promise<void> {
  const { error } = await supabase.from('products').insert(p)
  if (error) throw error
}

export async function deleteProduct(id: string) {
  const { error } = await supabase.from('products').delete().eq('id', id)
  if (error) throw error
}

/* ---------------- SEO-страницы ---------------- */

/** Объявления по марке/модели (+ город) — для страниц «Запчасти на X» */
export async function fetchByCar(
  brand: string,
  model: string,
  city?: string,
  max = 48,
): Promise<Listing[]> {
  let q = supabase
    .from('listings').select('*').eq('status', 'active').eq('brand', brand)
    .ilike('model', `%${model}%`)
  if (city) q = q.eq('city', city)
  const { data } = await q.order('bumpedAt', { ascending: false }).limit(max)
  return (data as Listing[]) || []
}

/** Сколько активных объявлений по каждой модели — для витрины моделей */
export async function countByModels(): Promise<Record<string, number>> {
  const { data } = await supabase
    .from('listings').select('brand,model').eq('status', 'active').limit(2000)
  const out: Record<string, number> = {}
  for (const r of (data as { brand: string; model: string }[]) || []) {
    const key = `${r.brand}|${r.model}`.toLowerCase()
    out[key] = (out[key] || 0) + 1
  }
  return out
}

